import { useCallback, useRef, useState, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { Underline } from '@tiptap/extension-underline';
import { TextAlign } from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Highlight } from '@tiptap/extension-highlight';
import { Link } from '@tiptap/extension-link';
import { Image } from '@tiptap/extension-image';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { Placeholder } from '@tiptap/extension-placeholder';
import { Typography } from '@tiptap/extension-typography';
import { TaskList } from '@tiptap/extension-task-list';
import { TaskItem } from '@tiptap/extension-task-item';
import { FontFamily } from '@tiptap/extension-font-family';

interface TiptapEditorProps {
  content: string;
  onChange: (html: string) => void;
  placeholder?: string;
}

const fontFamilies = [
  { name: 'Inter', value: 'Inter, sans-serif' },
  { name: 'Georgia', value: 'Georgia, serif' },
  { name: 'Monospace', value: 'monospace' },
  { name: 'Arial', value: 'Arial, sans-serif' },
];

const colors = [
  '#1a1a2e', '#16213e', '#0f3460', '#e94560', '#533483',
  '#2d6a4f', '#40916c', '#52b788', '#95d5b2', '#d8f3dc',
  '#6c757d', '#343a40', '#495057', '#f8f9fa', '#e9ecef',
  '#d00000', '#e85d04', '#ffba08', '#370617', '#03071e',
];

export default function TiptapEditor({ content, onChange, placeholder = 'Start writing...' }: TiptapEditorProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showHighlightPicker, setShowHighlightPicker] = useState(false);
  const [showFontPicker, setShowFontPicker] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [showLinkInput, setShowLinkInput] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3, 4] },
      }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TextStyle,
      FontFamily.configure({ types: ['textStyle'] }),
      Color.configure({ types: ['textStyle'] }),
      Highlight.configure({ multicolor: true }),
      Link.configure({ openOnClick: false, HTMLAttributes: { class: 'text-primary-600 underline underline-offset-2 cursor-pointer' } }),
      Image.configure({ inline: false, allowBase64: true }),
      Table.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
      Placeholder.configure({ placeholder }),
      Typography,
      TaskList,
      TaskItem.configure({ nested: true }),
    ],
    content,
    onUpdate: ({ editor: ed }) => {
      onChange(ed.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[400px] px-6 py-4',
      },
    },
  });

  const prevContentRef = useRef(content);
  useEffect(() => {
    if (editor && content !== prevContentRef.current) {
      const currentContent = editor.getHTML();
      if (currentContent !== content) {
        editor.commands.setContent(content);
      }
    }
    prevContentRef.current = content;
  }, [content, editor]);

  const setLink = useCallback(() => {
    if (!editor) return;
    if (linkUrl === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      const url = linkUrl.startsWith('http') ? linkUrl : `https://${linkUrl}`;
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
    setShowLinkInput(false);
    setLinkUrl('');
  }, [editor, linkUrl]);

  const addImage = useCallback(() => {
    if (!editor) return;
    const url = window.prompt('Image URL:');
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  }, [editor]);

  const addTable = useCallback(() => {
    if (!editor) return;
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  }, [editor]);

  if (!editor) return null;

  const ToolbarBtn = ({ active, onClick, children, title }: { active?: boolean; onClick: () => void; children: React.ReactNode; title: string }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`w-8 h-8 flex items-center justify-center rounded-md text-xs transition-colors cursor-pointer ${
        active ? 'bg-primary-100 text-primary-700' : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
      }`}
    >
      {children}
    </button>
  );

  const ToolbarDivider = () => <div className="w-px h-5 bg-background-200 mx-0.5" />;

  return (
    <div className="border border-background-200 rounded-xl overflow-hidden bg-background-50">
      {/* Toolbar */}
      <div ref={menuRef} className="flex flex-wrap items-center gap-0.5 px-3 py-2 border-b border-background-200 bg-background-50 sticky top-0 z-10">
        {/* Headings */}
        <ToolbarBtn active={editor.isActive('heading', { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} title="Heading 1">
          <span className="font-bold text-sm">H1</span>
        </ToolbarBtn>
        <ToolbarBtn active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Heading 2">
          <span className="font-bold text-xs">H2</span>
        </ToolbarBtn>
        <ToolbarBtn active={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="Heading 3">
          <span className="font-bold text-[10px]">H3</span>
        </ToolbarBtn>
        <ToolbarBtn active={editor.isActive('heading', { level: 4 })} onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()} title="Heading 4">
          <span className="font-bold text-[9px]">H4</span>
        </ToolbarBtn>

        <ToolbarDivider />

        {/* Text formatting */}
        <ToolbarBtn active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} title="Bold (Ctrl+B)">
          <i className="ri-bold"></i>
        </ToolbarBtn>
        <ToolbarBtn active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italic (Ctrl+I)">
          <i className="ri-italic"></i>
        </ToolbarBtn>
        <ToolbarBtn active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Underline (Ctrl+U)">
          <i className="ri-underline"></i>
        </ToolbarBtn>
        <ToolbarBtn active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()} title="Strikethrough">
          <i className="ri-strikethrough"></i>
        </ToolbarBtn>
        <ToolbarBtn active={editor.isActive('code')} onClick={() => editor.chain().focus().toggleCode().run()} title="Inline Code">
          <i className="ri-code-line"></i>
        </ToolbarBtn>
        <ToolbarBtn active={editor.isActive('highlight')} onClick={() => editor.chain().focus().toggleHighlight().run()} title="Highlight">
          <i className="ri-mark-pen-line"></i>
        </ToolbarBtn>

        <ToolbarDivider />

        {/* Font family */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowFontPicker(!showFontPicker)}
            className="w-auto px-2 h-8 flex items-center gap-1 rounded-md text-xs text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            title="Font Family"
          >
            <i className="ri-font-sans-serif"></i>
            <i className="ri-arrow-down-s-line text-[10px]"></i>
          </button>
          {showFontPicker && (
            <div className="absolute top-full left-0 mt-1 bg-background-50 border border-background-200 rounded-lg shadow-lg py-1 z-50 min-w-[140px]">
              {fontFamilies.map((f) => (
                <button
                  key={f.value}
                  type="button"
                  onClick={() => { editor.chain().focus().setFontFamily(f.value).run(); setShowFontPicker(false); }}
                  className={`w-full text-left px-3 py-1.5 text-xs hover:bg-background-50 transition-colors cursor-pointer whitespace-nowrap ${editor.isActive('textStyle', { fontFamily: f.value }) ? 'text-primary-700 font-semibold bg-primary-50' : 'text-foreground-700'}`}
                >
                  {f.name}
                </button>
              ))}
            </div>
          )}
        </div>

        <ToolbarDivider />

        {/* Text color */}
        <div className="relative">
          <button
            type="button"
            onClick={() => { setShowColorPicker(!showColorPicker); setShowHighlightPicker(false); }}
            className="w-8 h-8 flex items-center justify-center rounded-md text-xs text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
            title="Text Color"
          >
            <i className="ri-font-color"></i>
          </button>
          {showColorPicker && (
            <div className="absolute top-full left-0 mt-1 bg-background-50 border border-background-200 rounded-lg shadow-lg p-2 z-50">
              <div className="grid grid-cols-5 gap-1">
                {colors.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => { editor.chain().focus().setColor(c).run(); setShowColorPicker(false); }}
                    className="w-6 h-6 rounded cursor-pointer border border-transparent hover:border-foreground-300 transition-colors"
                    style={{ backgroundColor: c }}
                    title={c}
                  />
                ))}
              </div>
              <button
                type="button"
                onClick={() => { editor.chain().focus().unsetColor().run(); setShowColorPicker(false); }}
                className="w-full mt-1.5 text-[10px] text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer text-center"
              >
                Clear color
              </button>
            </div>
          )}
        </div>

        {/* Highlight color */}
        <div className="relative">
          <button
            type="button"
            onClick={() => { setShowHighlightPicker(!showHighlightPicker); setShowColorPicker(false); }}
            className="w-8 h-8 flex items-center justify-center rounded-md text-xs text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
            title="Background Color"
          >
            <i className="ri-contrast-drop-2-line"></i>
          </button>
          {showHighlightPicker && (
            <div className="absolute top-full left-0 mt-1 bg-background-50 border border-background-200 rounded-lg shadow-lg p-2 z-50">
              <div className="grid grid-cols-5 gap-1">
                {colors.map((c) => (
                  <button
                    key={`hl-${c}`}
                    type="button"
                    onClick={() => { editor.chain().focus().toggleHighlight({ color: c }).run(); setShowHighlightPicker(false); }}
                    className="w-6 h-6 rounded cursor-pointer border border-transparent hover:border-foreground-300 transition-colors"
                    style={{ backgroundColor: c }}
                    title={c}
                  />
                ))}
              </div>
              <button
                type="button"
                onClick={() => { editor.chain().focus().unsetHighlight().run(); setShowHighlightPicker(false); }}
                className="w-full mt-1.5 text-[10px] text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer text-center"
              >
                Clear highlight
              </button>
            </div>
          )}
        </div>

        <ToolbarDivider />

        {/* Alignment */}
        <ToolbarBtn active={editor.isActive({ textAlign: 'left' })} onClick={() => editor.chain().focus().setTextAlign('left').run()} title="Align Left">
          <i className="ri-align-left"></i>
        </ToolbarBtn>
        <ToolbarBtn active={editor.isActive({ textAlign: 'center' })} onClick={() => editor.chain().focus().setTextAlign('center').run()} title="Align Center">
          <i className="ri-align-center"></i>
        </ToolbarBtn>
        <ToolbarBtn active={editor.isActive({ textAlign: 'right' })} onClick={() => editor.chain().focus().setTextAlign('right').run()} title="Align Right">
          <i className="ri-align-right"></i>
        </ToolbarBtn>
        <ToolbarBtn active={editor.isActive({ textAlign: 'justify' })} onClick={() => editor.chain().focus().setTextAlign('justify').run()} title="Justify">
          <i className="ri-align-justify"></i>
        </ToolbarBtn>

        <ToolbarDivider />

        {/* Lists */}
        <ToolbarBtn active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Bullet List">
          <i className="ri-list-unordered"></i>
        </ToolbarBtn>
        <ToolbarBtn active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Numbered List">
          <i className="ri-list-ordered"></i>
        </ToolbarBtn>
        <ToolbarBtn active={editor.isActive('taskList')} onClick={() => editor.chain().focus().toggleTaskList().run()} title="Checklist">
          <i className="ri-checkbox-line"></i>
        </ToolbarBtn>

        <ToolbarDivider />

        {/* Blocks */}
        <ToolbarBtn active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Quote">
          <i className="ri-double-quotes-l"></i>
        </ToolbarBtn>
        <ToolbarBtn active={editor.isActive('codeBlock')} onClick={() => editor.chain().focus().toggleCodeBlock().run()} title="Code Block">
          <i className="ri-code-box-line"></i>
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Horizontal Divider">
          <i className="ri-separator"></i>
        </ToolbarBtn>

        <ToolbarDivider />

        {/* Link */}
        <div className="relative">
          <ToolbarBtn active={editor.isActive('link')} onClick={() => {
            const prev = editor.getAttributes('link').href || '';
            setLinkUrl(prev);
            setShowLinkInput(!showLinkInput);
          }} title="Link">
            <i className="ri-link"></i>
          </ToolbarBtn>
          {showLinkInput && (
            <div className="absolute top-full left-0 mt-1 bg-background-50 border border-background-200 rounded-lg shadow-lg p-3 z-50 flex items-center gap-2 min-w-[260px]">
              <input
                type="text"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://..."
                className="flex-1 px-2 py-1.5 text-xs border border-background-200 rounded-md outline-none focus:ring-1 focus:ring-primary-400"
                onKeyDown={(e) => { if (e.key === 'Enter') setLink(); }}
                autoFocus
              />
              <button type="button" onClick={setLink} className="px-2 py-1 bg-primary-500 text-white text-xs rounded hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">Apply</button>
              <button type="button" onClick={() => { editor.chain().focus().unsetLink().run(); setShowLinkInput(false); }} className="text-foreground-400 hover:text-foreground-600 cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>
          )}
        </div>

        {/* Image */}
        <ToolbarBtn onClick={addImage} title="Insert Image">
          <i className="ri-image-line"></i>
        </ToolbarBtn>

        {/* Table */}
        <ToolbarBtn onClick={addTable} title="Insert Table">
          <i className="ri-table-line"></i>
        </ToolbarBtn>

        <ToolbarDivider />

        {/* Undo / Redo */}
        <ToolbarBtn onClick={() => editor.chain().focus().undo().run()} title="Undo (Ctrl+Z)">
          <i className="ri-arrow-go-back-line"></i>
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().redo().run()} title="Redo (Ctrl+Shift+Z)">
          <i className="ri-arrow-go-forward-line"></i>
        </ToolbarBtn>
      </div>

      {/* Editor content */}
      <EditorContent editor={editor} />
    </div>
  );
}